import { useLayoutEffect, useRef, useState } from 'react';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { asDate, clampDate, dateKey, displayDate, fullDate, inBounds, shiftMonth } from './dates';

const months = Array.from({ length: 12 }, (_, month) => new Date(2026, month, 1).toLocaleDateString('en-GB', { month: 'long' }));
const weekdays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export default function CalendarPanel({ id, label, value, minDate, maxDate, position, anchor, onSelect, onClear, onClose }) {
    const today = asDate(new Date());
    const min = asDate(minDate), max = asDate(maxDate);
    const initial = clampDate(value || today, min, max);
    const [focused, setFocused] = useState(initial);
    const [month, setMonth] = useState(new Date(initial.getFullYear(), initial.getMonth(), 1));
    const panel = useRef(null);
    const focusRequested = useRef(true);
    useLayoutEffect(() => {
        const focusDay = () => panel.current.querySelector(`[data-date="${dateKey(focused)}"]`)?.focus({ preventScroll: true });
        if (focusRequested.current && position.visibility !== 'hidden') {
            focusDay();
            focusRequested.current = false;
        }
        const containFocus = (event) => {
            if (position.visibility !== 'hidden' && !panel.current.contains(event.target) && !anchor.current?.contains(event.target)) focusDay();
        };
        document.addEventListener('focusin', containFocus);
        return () => document.removeEventListener('focusin', containFocus);
    }, [focused, month, position.visibility, anchor]);

    const moveFocus = (date) => {
        const next = clampDate(date, min, max);
        focusRequested.current = true;
        setFocused(next);
        setMonth(new Date(next.getFullYear(), next.getMonth(), 1));
    };
    const changeMonth = (next) => {
        const date = clampDate(next, min, max);
        setMonth(new Date(date.getFullYear(), date.getMonth(), 1));
        setFocused(date);
    };
    const dayKeyDown = (event, date) => {
        let next;
        const offset = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 }[event.key];
        if (offset) next = new Date(date.getFullYear(), date.getMonth(), date.getDate() + offset);
        if (event.key === 'Home') next = new Date(date.getFullYear(), date.getMonth(), date.getDate() - (date.getDay() + 6) % 7);
        if (event.key === 'End') next = new Date(date.getFullYear(), date.getMonth(), date.getDate() + 6 - (date.getDay() + 6) % 7);
        if (event.key === 'PageUp' || event.key === 'PageDown') next = shiftMonth(date, (event.key === 'PageUp' ? -1 : 1) * (event.shiftKey ? 12 : 1));
        if (next) { event.preventDefault(); moveFocus(next); }
    };
    const keyDown = (event) => {
        event.stopPropagation(); // Keep an enclosing dialog from handling calendar keys.
        if (event.key === 'Escape') { event.preventDefault(); onClose(); }
        if (event.key === 'Tab') {
            const controls = Array.from(panel.current.querySelectorAll('button:not(:disabled), select, [tabindex="0"]')).filter((element) => element.tabIndex >= 0);
            const first = controls[0], last = controls[controls.length - 1];
            if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
            if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
        }
    };
    const year = month.getFullYear(), monthIndex = month.getMonth();
    const startYear = min?.getFullYear() ?? Math.min(1900, year);
    const endYear = max?.getFullYear() ?? Math.max(today.getFullYear() + 10, year);
    const years = Array.from({ length: endYear - startYear + 1 }, (_, index) => startYear + index);
    const firstOffset = (month.getDay() + 6) % 7;
    const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
    const days = Array.from({ length: Math.ceil((firstOffset + daysInMonth) / 7) * 7 }, (_, index) => new Date(year, monthIndex, 1 - firstOffset + index));
    const previous = shiftMonth(month, -1), next = shiftMonth(month, 1);
    return <div ref={panel} id={id} className="portal-calendar-panel" role="dialog" aria-modal="true" aria-label={`Choose ${label.toLowerCase()}`} style={position} onKeyDown={keyDown}>
        <div className="portal-calendar-caption"><span>{label}</span><strong>{value ? displayDate(value) : 'Choose a date'}</strong></div>
        <div className="portal-calendar-header">
            <button type="button" className="portal-calendar-nav" aria-label="Previous month" disabled={!!min && new Date(year, monthIndex, 0) < min} onClick={() => changeMonth(previous)}><FiChevronLeft aria-hidden="true" /></button>
            <div className="portal-calendar-selects">
                <select aria-label="Month" value={monthIndex} onChange={(event) => changeMonth(new Date(year, Number(event.target.value), 1))}>{months.map((name, index) => <option key={name} value={index} disabled={(!!min && new Date(year, index + 1, 0) < min) || (!!max && new Date(year, index, 1) > max)}>{name}</option>)}</select>
                <select aria-label="Year" value={year} onChange={(event) => changeMonth(new Date(Number(event.target.value), monthIndex, 1))}>{years.map((item) => <option key={item}>{item}</option>)}</select>
            </div>
            <button type="button" className="portal-calendar-nav" aria-label="Next month" disabled={!!max && next > max} onClick={() => changeMonth(next)}><FiChevronRight aria-hidden="true" /></button>
        </div>
        <span className="sr-only" aria-live="polite">{months[monthIndex]} {year}</span>
        <div className="portal-calendar-weekdays" aria-hidden="true">{weekdays.map((day) => <span key={day}>{day}</span>)}</div>
        <div className="portal-calendar-days" role="group" aria-label={`${months[monthIndex]} ${year}`}>
            {days.map((date) => <button key={dateKey(date)} type="button" data-date={dateKey(date)} className={`portal-calendar-day${date.getMonth() !== monthIndex ? ' is-outside' : ''}${dateKey(date) === dateKey(value) ? ' is-selected' : ''}${dateKey(date) === dateKey(today) ? ' is-today' : ''}`} aria-label={fullDate(date)} aria-pressed={dateKey(date) === dateKey(value)} aria-current={dateKey(date) === dateKey(today) ? 'date' : undefined} disabled={!inBounds(date, min, max)} tabIndex={dateKey(date) === dateKey(focused) ? 0 : -1} onFocus={() => setFocused(date)} onKeyDown={(event) => dayKeyDown(event, date)} onClick={() => onSelect(date)}>{date.getDate()}</button>)}
        </div>
        <footer className="portal-calendar-footer"><button type="button" disabled={!inBounds(today, min, max)} onClick={() => onSelect(today)}>Today</button><button type="button" disabled={!value} onClick={onClear}>Clear date</button></footer>
        <p className="portal-calendar-hint">Arrow keys to move · Enter to select</p>
    </div>;
}
